// searchsync-autocomplete-component.js

import { autocomplete } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { getInstance, registerWidget, createSearchClient } from './searchsync-create-instance';

/**
 * Autocomplete widget: a search input with a live suggestions dropdown, rendered with
 * @algolia/autocomplete-js. Backend-agnostic — item sources fetch through the shared
 * createSearchClient (Algolia / Meilisearch / Local all expose `.search(requests)`).
 *
 * The dropdown is composed of user-selectable SOURCES (Phase 2), so the plugin's users decide how
 * suggestions behave:
 *   - Hit previews   — matching records; selecting one navigates to its link (all backends).
 *   - Query completions — distinct title values from matching records, as query text (all backends).
 *   - Recent searches   — this browser's recent queries, from localStorage (all backends).
 *   - Query Suggestions — popular searches from an Algolia Query Suggestions index (Algolia only,
 *                         via @algolia/autocomplete-plugin-query-suggestions).
 *
 * On-select behavior (for query-style selections + submit) is 'filter' (sync the query into the
 * on-page InstantSearch instance) or 'redirect' (go to a results page). Selecting a hit-preview
 * with a link always navigates to that link.
 *
 * IMPORTANT: the mount element carries data-init="false" and is part of the shared instance-start
 * barrier (see searchsync-create-instance.js's registerWidget) — we MUST call registerWidget after
 * mounting or no InstantSearch instance on the page starts.
 */

const RECENT_KEY = 'searchsync-recent-searches';
const RECENT_MAX = 6;

function readRecent() {
  try {
    const v = JSON.parse( window.localStorage.getItem( RECENT_KEY ) );
    return Array.isArray( v ) ? v.filter( ( x ) => typeof x === 'string' ) : [];
  } catch ( _ ) {
    return [];
  }
}

function pushRecent( query ) {
  const q = ( query || '' ).trim();
  if ( ! q ) return;
  try {
    const list = readRecent().filter( ( x ) => x.toLowerCase() !== q.toLowerCase() );
    list.unshift( q );
    window.localStorage.setItem( RECENT_KEY, JSON.stringify( list.slice( 0, RECENT_MAX ) ) );
  } catch ( _ ) {
    // localStorage unavailable (private mode / disabled) — recent searches just won't persist.
  }
}

function initAutocompleteWidget() {
  const tgblAlgoliaData = jQuery.parseJSON( window.atob( tgblSearchsyncDataEncoded ) );

  const client = createSearchClient( tgblAlgoliaData );
  const backend = tgblAlgoliaData.backend;

  const elements = document.querySelectorAll( "[data-filter-type='autocomplete']" );

  elements.forEach( ( element ) => {
    const indexName = element.getAttribute( 'data-index' );
    if ( ! indexName ) return;

    const search = getInstance( indexName );
    if ( ! search ) return;

    const widgetID = element.getAttribute( 'data-id' );
    const cfg = tgblAlgoliaData[ widgetID ] || {};
    const titleAttr = cfg.title_attribute || 'title';
    const urlAttr = cfg.url_attribute === 'none' ? '' : ( cfg.url_attribute || 'permalink' );
    const hitsCount = parseInt( cfg.hits_count, 10 ) || 5;
    const onSelectMode = cfg.on_select === 'redirect' ? 'redirect' : 'filter';
    const redirectUrl = cfg.redirect_url || '';
    const placeholder = cfg.placeholder || '';

    // Source toggles. Hit previews default on (also covers legacy widgets with no toggle stored).
    const showHits = cfg.source_hits !== false;
    const showCompletions = cfg.source_completions === true;
    const showRecent = cfg.source_recent === true;
    const showSuggestions =
      cfg.source_suggestions === true && backend === 'algolia' && !! cfg.suggestions_index;

    // Apply a chosen query: remember it, then either filter the on-page results or redirect.
    const runQuery = ( query ) => {
      pushRecent( query );
      if ( onSelectMode === 'redirect' && redirectUrl ) {
        const sep = redirectUrl.indexOf( '?' ) === -1 ? '?' : '&';
        window.location.href = `${ redirectUrl }${ sep }s=${ encodeURIComponent( query ) }`;
        return;
      }
      // 'filter' mode: drive the on-page results. setUiState (function form) merges with the
      // instance's routing-backed state instead of clobbering other refinements.
      if ( search && typeof search.setUiState === 'function' ) {
        search.setUiState( ( prev ) => ( {
          ...prev,
          [ indexName ]: { ...( prev[ indexName ] || {} ), query, page: 1 },
        } ) );
      }
    };

    // ---- Sources ------------------------------------------------------------------------------
    const buildSources = ( { query } ) => {
      const sources = [];

      if ( showRecent ) {
        const q = ( query || '' ).toLowerCase();
        const recent = readRecent()
          .filter( ( r ) => ! q || r.toLowerCase().includes( q ) )
          .slice( 0, 5 );
        if ( recent.length ) {
          sources.push( {
            sourceId: 'recent',
            getItems() {
              return recent.map( ( label ) => ( { label } ) );
            },
            templates: {
              header( { html } ) {
                return html`<span class="aa-SourceHeaderTitle">Recent searches</span><div class="aa-SourceHeaderLine"></div>`;
              },
              item( { item, html } ) {
                return html`<div class="aa-ItemContent"><div class="aa-ItemContentBody"><div class="aa-ItemContentTitle">${ item.label }</div></div></div>`;
              },
            },
            onSelect( { item, setQuery } ) {
              setQuery( item.label );
              runQuery( item.label );
            },
          } );
        }
      }

      if ( showCompletions && query ) {
        sources.push( {
          sourceId: 'completions',
          getItems() {
            return client
              .search( [ { indexName, params: { query, hitsPerPage: 5 } } ] )
              .then( ( { results } ) => {
                const hits = ( results && results[ 0 ] && results[ 0 ].hits ) || [];
                const seen = new Set();
                const out = [];
                hits.forEach( ( h ) => {
                  const t = h[ titleAttr ];
                  if ( t != null && ! seen.has( String( t ) ) ) {
                    seen.add( String( t ) );
                    out.push( { label: String( t ) } );
                  }
                } );
                return out.slice( 0, 5 );
              } );
          },
          templates: {
            header( { html } ) {
              return html`<span class="aa-SourceHeaderTitle">Suggestions</span><div class="aa-SourceHeaderLine"></div>`;
            },
            item( { item, html } ) {
              return html`<div class="aa-ItemContent"><div class="aa-ItemContentBody"><div class="aa-ItemContentTitle">${ item.label }</div></div></div>`;
            },
          },
          onSelect( { item, setQuery } ) {
            setQuery( item.label );
            runQuery( item.label );
          },
        } );
      }

      if ( showHits && query ) {
        sources.push( {
          sourceId: 'hits',
          getItems() {
            return client
              .search( [ { indexName, params: { query, hitsPerPage: hitsCount } } ] )
              .then( ( { results } ) => ( results && results[ 0 ] && results[ 0 ].hits ) || [] );
          },
          getItemUrl( { item } ) {
            return ( urlAttr && item[ urlAttr ] ) || undefined;
          },
          templates: {
            header( { html } ) {
              return html`<span class="aa-SourceHeaderTitle">Results</span><div class="aa-SourceHeaderLine"></div>`;
            },
            item( { item, html } ) {
              const title = item[ titleAttr ] != null ? String( item[ titleAttr ] ) : '';
              return html`<div class="aa-ItemContent"><div class="aa-ItemContentBody"><div class="aa-ItemContentTitle">${ title }</div></div></div>`;
            },
            noResults() {
              return 'No matches.';
            },
          },
          onSelect( { item, state } ) {
            // A custom onSelect suppresses autocomplete-js's built-in navigation, so navigate
            // explicitly when the hit has a link; otherwise drive the results / redirect.
            const url = urlAttr ? item[ urlAttr ] : '';
            if ( url ) {
              pushRecent( state && state.query );
              window.location.assign( url );
              return;
            }
            runQuery( item[ titleAttr ] != null ? String( item[ titleAttr ] ) : '' );
          },
        } );
      }

      return sources;
    };

    // ---- Query Suggestions plugin (Algolia only) ----------------------------------------------
    const plugins = [];
    if ( showSuggestions ) {
      try {
        plugins.push(
          createQuerySuggestionsPlugin( {
            searchClient: client,
            indexName: cfg.suggestions_index,
            getSearchParams() {
              return { hitsPerPage: 5 };
            },
            transformSource( { source } ) {
              return {
                ...source,
                onSelect( { item, setQuery } ) {
                  const q = item.query != null ? String( item.query ) : '';
                  setQuery( q );
                  runQuery( q );
                },
              };
            },
          } )
        );
      } catch ( _ ) {
        // Defensive: if the QS plugin can't initialise (e.g. client shape), skip it rather than
        // breaking the whole widget.
      }
    }

    autocomplete( {
      container: element,
      placeholder,
      openOnFocus: showRecent, // open on focus so recent searches can show with an empty query
      plugins,
      getSources: buildSources,
      onSubmit( { state } ) {
        runQuery( state.query );
      },
    } );

    // Complete the shared instance-start barrier (see module docblock).
    registerWidget( element );
  } );
}

export { initAutocompleteWidget };
